import React, { useMemo, useState } from 'react';
import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { AppShell } from '../../../components/layout/AppShell';
import { SubNav, studioNav } from '../../../components/layout/SubNav';
import { Card, KeyValue, SectionHeading } from '../../../components/ui/Panels';
import { Button, IconButton } from '../../../components/ui/Button';
import { Field, Input, Switch } from '../../../components/ui/Field';
import { useSession } from '../../../contexts/SessionContext';
import { useAppData } from '../../../contexts/AppDataContext';
import { useLocale } from '../../../contexts/LocaleContext';
import { longDate } from '../../../utils/format';
import { cn } from '../../../utils/cn';
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
function monthGrid(year: number, month: number): (string | null)[] {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7;
  const days = new Date(year, month + 1, 0).getDate();
  const cells: (string | null)[] = Array.from({
    length: offset
  }, () => null);
  for (let d = 1; d <= days; d += 1) {
    cells.push(`${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }
  return cells;
}
export function Availability() {
  const {
    chef
  } = useSession();
  const {
    bookings
  } = useAppData();
  const {
    locale
  } = useLocale();
  const [cursor, setCursor] = useState({
    year: 2026,
    month: 8
  });
  const [blocked, setBlocked] = useState<string[]>(['2026-09-12', '2026-09-13', '2026-09-27']);
  const [weekdays, setWeekdays] = useState<string[]>(['Wed', 'Thu', 'Fri', 'Sat', 'Sun']);
  const [leadDays, setLeadDays] = useState(3);
  const [maxPerWeek, setMaxPerWeek] = useState(4);
  const [pause, setPause] = useState(false);
  const booked = useMemo(() => new Map(bookings.filter(b => b.chefId === chef.id).map(b => [b.date, b])), [bookings, chef.id]);
  const cells = monthGrid(cursor.year, cursor.month);
  const monthName = new Date(cursor.year, cursor.month, 1).toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric'
  });
  const shift = (delta: number) => {
    const next = new Date(cursor.year, cursor.month + delta, 1);
    setCursor({
      year: next.getFullYear(),
      month: next.getMonth()
    });
  };
  const toggleBlock = (iso: string) => setBlocked(blocked.includes(iso) ? blocked.filter(d => d !== iso) : [...blocked, iso]);
  const openThisMonth = cells.filter(iso => iso && !blocked.includes(iso) && !booked.has(iso)).length;
  return <AppShell title="Availability" subtitle={`Next free date ${longDate(chef.nextAvailable, locale)}`} data-id="element-1243" data-mp="3daiq9g788wnj">
      <SubNav items={studioNav} data-id="element-1244" data-mp="2ydhy5bl4izoj" />

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-1245" data-mp="33ai65s1jacia">
        <Card className="p-5" data-id="element-1246" data-mp="33cunc2zgwiua">
          <div className="flex items-center justify-between gap-3" data-id="element-1247" data-mp="wjsh4q02xbnc">
            <h2 className="font-display text-lg font-semibold text-ink" data-id="element-1248" data-mp="wihdqrajq5wr">{monthName}</h2>
            <div className="flex gap-1.5" data-id="element-1249" data-mp="11iltgf3mqwlx">
              <IconButton label="Previous month" onClick={() => shift(-1)} data-id="element-1250" data-mp="2m0wg4hoc34sd">
                <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1251" data-mp="2h2cruam9pgwu" />
              </IconButton>
              <IconButton label="Next month" onClick={() => shift(1)} data-id="element-1252" data-mp="2vr8v7uddq5r4">
                <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1253" data-mp="2vsk0jymh3lkc" />
              </IconButton>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-7 gap-1.5" role="grid" aria-label={`Availability for ${monthName}`} data-id="element-1254" data-mp="34rnk13pjy2zd">
            {WEEKDAYS.map(day => <div key={day} className="pb-1 text-center text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1255" data-mp="30pshxwzsrlkj">
                {day}
              </div>)}
            {cells.map((iso, i) => {
            if (!iso) return <div key={`pad-${i}`} data-id="element-1256" data-mp="1kc2fsvs8n7dj" />;
            const day = Number(iso.slice(-2));
            const bookingHere = booked.get(iso);
            const isBlocked = blocked.includes(iso);
            return <button key={iso} type="button" onClick={() => !bookingHere && toggleBlock(iso)} disabled={!!bookingHere} aria-label={`${longDate(iso, locale)} — ${bookingHere ? `booked, ${bookingHere.guests} guests` : isBlocked ? 'blocked' : 'available'}`} className={cn('flex aspect-square flex-col items-center justify-center rounded-lg border text-[13px] transition-colors duration-150 ease-out', bookingHere ? 'cursor-default border-primary bg-primary text-on-primary' : isBlocked ? 'border-hairline bg-oat text-ink-3 line-through' : 'border-hairline text-ink hover:border-primary/50 hover:bg-primary-soft')} data-id="element-1257" data-mp="1guo5s1i3rbzm" data-mpi={i}>
                  <span className="tnum font-medium" data-id="element-1258" data-mp="kayvyz6tf3at">{day}</span>
                  {bookingHere && <span className="tnum text-[10px] opacity-85" data-id="element-1259" data-mp="2xli8e3yj49la">{bookingHere.guests}p</span>}
                </button>;
          })}
          </div>

          <div className="mt-5 flex flex-wrap gap-4 border-t border-hairline pt-4 text-[12px] text-ink-2" data-id="element-1260" data-mp="2olv49aef0poj">
            <span className="flex items-center gap-1.5" data-id="element-1261" data-mp="2jn0o6g7u1f84">
              <span className="h-3 w-3 rounded bg-primary" aria-hidden="true" data-id="element-1262" data-mp="2jmikwzmr60qp" /> Booked
            </span>
            <span className="flex items-center gap-1.5" data-id="element-1263" data-mp="2eoyxpa82udmm">
              <span className="h-3 w-3 rounded bg-oat ring-1 ring-hairline" aria-hidden="true" data-id="element-1264" data-mp="29wp67prbd5i8" /> Blocked by you
            </span>
            <span className="flex items-center gap-1.5" data-id="element-1265" data-mp="25vvo5d0jykx9">
              <span className="h-3 w-3 rounded ring-1 ring-hairline" aria-hidden="true" data-id="element-1266" data-mp="20wsoy97978x7" /> Open
            </span>
            <span className="ml-auto" data-id="element-1267" data-mp="1vw4oyy11rhqu">Tap any open date to block it.</span>
          </div>
        </Card>

        <aside className="space-y-4" data-id="element-1268" data-mp="1qz4k2qfxol1b">
          <Card className="p-5" data-id="element-1269" data-mp="1r0659nqnynwr">
            <div className="flex items-center gap-2" data-id="element-1270" data-mp="1h9xxsr6qf98q">
              <CalendarDaysIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-1271" data-mp="1h963djovfhk1" />
              <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1272" data-mp="1mb538jlejwc8">This month</h2>
            </div>
            <dl className="mt-3" data-id="element-1273" data-mp="18c5fde5prlh4">
              <KeyValue label="Open dates" value={String(openThisMonth)} data-id="element-1274" data-mp="1caut57unz51a" />
              <KeyValue label="Booked" value={String(cells.filter(iso => iso && booked.has(iso)).length)} data-id="element-1275" data-mp="200ysh30tkimh" />
              <KeyValue label="Blocked" value={String(cells.filter(iso => iso && blocked.includes(iso)).length)} data-id="element-1276" data-mp="24zs7x0zo2jpa" />
            </dl>
          </Card>

          <Card className="p-5" data-id="element-1277" data-mp="1r859ovm5z13v">
            <SectionHeading level={2} title="Your working week" data-id="element-1278" data-mp="1va8ptxjs2tvh" />
            <div className="mt-3 flex flex-wrap gap-1.5" data-id="element-1279" data-mp="1v8p4hsj9lsmu">
              {WEEKDAYS.map(day => {
              const on = weekdays.includes(day);
              return <button key={day} type="button" aria-pressed={on} onClick={() => setWeekdays(on ? weekdays.filter(d => d !== day) : [...weekdays, day])} className={cn('rounded-full border px-3 py-1.5 text-[12px] font-medium transition-colors duration-150 ease-out', on ? 'border-primary bg-primary-soft text-primary' : 'border-hairline text-ink-3 hover:text-ink')} data-id="element-1280" data-mp="2nrkyypv6duud">
                    {day}
                  </button>;
            })}
            </div>

            <div className="mt-5 grid gap-4" data-id="element-1281" data-mp="3aw15tmmzgsm">
              <Field label="Notice required" id="av-lead" hint="days before a date can be requested" data-id="element-1282" data-mp="3bnokh62y87t">
                <Input id="av-lead" type="number" min={0} max={30} value={leadDays} onChange={e => setLeadDays(Number(e.target.value) || 0)} className="tnum" data-id="element-1283" data-mp="3ufm4jra1cihv" />
              </Field>
              <Field label="Maximum bookings a week" id="av-max" data-id="element-1284" data-mp="337m0waqn75dh">
                <Input id="av-max" type="number" min={1} max={14} value={maxPerWeek} onChange={e => setMaxPerWeek(Number(e.target.value) || 1)} className="tnum" data-id="element-1285" data-mp="389jr5oxgn0fx" />
              </Field>
            </div>

            <div className="mt-3 border-t border-hairline" data-id="element-1286" data-mp="3qz9j849u9845">
              <Switch checked={pause} onChange={setPause} label="Pause my listing" description="You stay visible to past guests but drop out of search until you switch this back." data-id="element-1287" data-mp="3qxop2frfrr6t" />
            </div>

            <Button type="button" variant="action" full className="mt-4" data-id="element-1288" data-mp="21ld4djeenxz1">
              Save availability
            </Button>
          </Card>
        </aside>
      </div>
    </AppShell>;
}